import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsers } from '@/store/user';
import { User, Mail, Check, ArrowLeft, ShieldCheck, Building, Calendar, Activity } from 'lucide-react';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { InfoGrid } from '@/components/ui/InfoGrid';
import { useLayout } from '@/contexts/layout-context';
import { useTenants } from '@/store/tenant';

export const CreateUserPage = () => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [tenantId, setTenantId] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [validFrom, setValidFrom] = useState('');
    const [validTill, setValidTill] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const createUser = useUsers((state) => state.create);
    const { tenants, list: listTenants } = useTenants();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    useEffect(() => {
        setBreadcrumbs([
            { name: 'Users', to: '/m/users' },
            { name: 'Create' }
        ]);
        listTenants();
    }, [setBreadcrumbs, listTenants]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submittingRef.current) return;
        submittingRef.current = true;
        setIsSubmitting(true);
        try {
            await createUser({
                email,
                name,
                tenant_id: tenantId || undefined,
                status: isActive ? 1 : 2,
                valid_from: validFrom ? new Date(validFrom).toISOString() : undefined,
                valid_till: validTill ? new Date(validTill).toISOString() : undefined,
            } as any);
            navigate('/m/users');
        } catch {
            // Error toast is handled automatically by store
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-4 lg:p-8 max-w-4xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title="Register User"
                subtitle="Create a new global identity for platform access."
                actions={
                    <Button variant="ghost" onClick={() => navigate('/m/users')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back to List
                    </Button>
                }
            />

            <div className="mt-8 animate-in slide-in-from-bottom-4 duration-500">
                <Card
                    title="User Profile Information"
                    subtitle="Basic contact details for the new platform user."
                    className="glass"
                >
                    <form onSubmit={submit} className="space-y-8">
                        <InfoGrid cols={2}>
                            <InputField
                                label="Full Name"
                                placeholder="e.g. John Doe"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                icon={<User className="w-4 h-4" />}
                                helpText="The primary display name for this user."
                            />
                            <InputField
                                label="Email Address"
                                type="email"
                                placeholder="john@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                icon={<Mail className="w-4 h-4" />}
                                helpText="Used for authentication and notifications."
                            />

                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Tenant
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                        <Building className="w-4 h-4" />
                                    </div>
                                    <select
                                        className="w-full px-4 py-2 pl-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm transition-all duration-200 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 dark:text-white appearance-none"
                                        value={tenantId}
                                        onChange={(e) => setTenantId(e.target.value)}
                                        required
                                    >
                                        <option value="" disabled>Select a tenant...</option>
                                        {tenants.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Assign user to a specific tenant.</p>
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Account Status
                                </label>
                                <div
                                    className="relative flex items-center w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer transition-all duration-200 hover:border-primary/50"
                                    onClick={() => setIsActive(!isActive)}
                                >
                                    <Activity className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <div className="pl-7 flex items-center justify-between w-full">
                                        <span className={`text-sm ${isActive ? 'text-slate-900 dark:text-white font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
                                            {isActive ? 'Active User' : 'Inactive User'}
                                        </span>
                                        <div
                                            className={`flex w-9 h-5 rounded-full p-0.5 transition-colors duration-200 ease-in-out ${isActive ? 'bg-green-600 dark:bg-green-800' : 'bg-slate-300 dark:bg-slate-700'}`}
                                        >
                                            <div
                                                className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200 ease-in-out ${isActive ? 'translate-x-4' : 'translate-x-0'}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Set the initial activation status.</p>
                            </div>

                            <InputField
                                label="Valid From (Optional)"
                                type="datetime-local"
                                value={validFrom}
                                onChange={(e) => setValidFrom(e.target.value)}
                                icon={<Calendar className="w-4 h-4" />}
                                helpText="When this account should become active."
                            />

                            <InputField
                                label="Valid Till (Optional)"
                                type="datetime-local"
                                value={validTill}
                                onChange={(e) => setValidTill(e.target.value)}
                                icon={<Calendar className="w-4 h-4" />}
                                helpText="When this account should expire."
                            />
                        </InfoGrid>

                        <div className="bg-orange-50/50 dark:bg-orange-500/5 p-4 rounded-xl border border-orange-100 dark:border-orange-500/10 flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-white dark:bg-slate-900 border border-orange-100 dark:border-orange-500/20 flex items-center justify-center text-orange-600 shrink-0 shadow-sm">
                                <ShieldCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-slate-900 dark:text-white">Security Note</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed italic">
                                    By registering this user, they will be granted baseline platform access.
                                    Further permissions can be assigned via Group memberships or Direct Roles after creation.
                                </p>
                            </div>
                        </div>

                        <div className="pt-8 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                            <Button variant="ghost" type="button" onClick={() => navigate('/m/users')}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                isLoading={isSubmitting}
                                className="min-w-40 gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Register Account
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};
